'use client';

import { createContext, useContext } from 'react';

type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const ButtonSizeContext = createContext<ButtonSize | null>(null);

/**
 * Provides a default button size to all descendant `<Button>` components.
 * An explicit `size` prop on the Button still wins.
 *
 * @example
 * ```tsx
 * <ButtonSizeProvider value="lg">
 *   <Button>Uses lg</Button>
 *   <Button size="sm">Still sm</Button>
 * </ButtonSizeProvider>
 * ```
 */
export const ButtonSizeProvider = ButtonSizeContext.Provider;

/**
 * Returns the size set by the nearest `ButtonSizeProvider`, or `null` when
 * there is none or the nearest one resets it with `value={null}`.
 *
 * @example
 * ```tsx
 * const contextSize = useButtonSizeContext();
 * const size = sizeProp ?? contextSize ?? 'md';
 * ```
 */
export const useButtonSizeContext = () => useContext(ButtonSizeContext);
